/**
 * Time Range Picker Component
 * Provides hour/minute/AM-PM dropdowns for selecting time ranges
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Clock, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TimeValue {
  hour: string;
  minute: string;
  period: 'AM' | 'PM';
}

interface TimeSlot {
  startTime: string; // 24h format: "HH:mm:ss"
  endTime: string; // 24h format: "HH:mm:ss"
}

interface TimeRangePickerProps {
  startTime: string; // 24h format: "HH:mm:ss"
  endTime: string; // 24h format: "HH:mm:ss"
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  minTime?: string; // 24h format: "HH:mm:ss" - minimum allowed time (deprecated, use availableSlots)
  maxTime?: string; // 24h format: "HH:mm:ss" - maximum allowed time (deprecated, use availableSlots)
  availableSlots?: TimeSlot[]; // Available time slots - selection must be within one of these
  disabled?: boolean;
  className?: string;
}

// Generate hours 1-12
const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

// Generate minutes 00-55 in 5-minute intervals
const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

// Parse 24h time string to TimeValue
const parse24hTo12h = (time24: string): TimeValue | null => {
  if (!time24) {
    return null;
  }
  const [hoursStr, minutesStr] = time24.split(':');
  let hours = parseInt(hoursStr, 10);
  const period: 'AM' | 'PM' = hours >= 12 ? 'PM' : 'AM';
  
  if (hours === 0) {
    hours = 12;
  } else if (hours > 12) {
    hours = hours - 12;
  }
  
  return {
    hour: String(hours).padStart(2, '0'),
    minute: minutesStr?.padStart(2, '0') || '00',
    period,
  };
};

// Get default empty time value
const getEmptyTimeValue = (): TimeValue => ({ hour: '', minute: '', period: 'AM' });

// Convert TimeValue to 24h format string
const to24hFormat = (time: TimeValue): string => {
  let hours = parseInt(time.hour, 10);
  
  if (time.period === 'AM') {
    if (hours === 12) hours = 0;
  } else {
    if (hours !== 12) hours = hours + 12;
  }
  
  return `${String(hours).padStart(2, '0')}:${time.minute}:00`;
};

// Convert 24h time to minutes for comparison
const timeToMinutes = (time24: string): number => {
  const [hours, mins] = time24.split(':').map(Number);
  return hours * 60 + mins;
};

// Format time for display
const formatTimeDisplay = (time24: string): string => {
  if (!time24) return 'Select time';
  const { hour, minute, period } = parse24hTo12h(time24);
  return `${hour}:${minute} ${period}`;
};

const TimePicker: React.FC<{
  value: TimeValue;
  onChange: (value: TimeValue) => void;
  disabled?: boolean;
  label: string;
}> = ({ value, onChange, disabled, label }) => {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-1">
        {/* Hour */}
        <Select
          value={value.hour}
          onValueChange={(h) => onChange({ ...value, hour: h })}
          disabled={disabled}
        >
          <SelectTrigger className="w-[70px] h-9">
            <SelectValue placeholder="Hr" />
          </SelectTrigger>
          <SelectContent className="bg-background z-[100] max-h-48">
            {hours.map((h) => (
              <SelectItem key={h} value={h}>
                {h}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-muted-foreground font-medium">:</span>

        {/* Minute */}
        <Select
          value={value.minute}
          onValueChange={(m) => onChange({ ...value, minute: m })}
          disabled={disabled}
        >
          <SelectTrigger className="w-[70px] h-9">
            <SelectValue placeholder="Min" />
          </SelectTrigger>
          <SelectContent className="bg-background z-[100] max-h-48">
            {minutes.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* AM/PM */}
        <Select
          value={value.period}
          onValueChange={(p) => onChange({ ...value, period: p as 'AM' | 'PM' })}
          disabled={disabled}
        >
          <SelectTrigger className="w-[70px] h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-background z-[100]">
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const TimeRangePicker: React.FC<TimeRangePickerProps> = ({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  minTime,
  maxTime,
  availableSlots,
  disabled = false,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [startValue, setStartValue] = useState<TimeValue>(() => parse24hTo12h(startTime) || getEmptyTimeValue());
  const [endValue, setEndValue] = useState<TimeValue>(() => parse24hTo12h(endTime) || getEmptyTimeValue());
  const [error, setError] = useState<string | null>(null);

  // Sync internal state with props
  useEffect(() => {
    setStartValue(parse24hTo12h(startTime) || getEmptyTimeValue());
  }, [startTime]);

  useEffect(() => {
    setEndValue(parse24hTo12h(endTime) || getEmptyTimeValue());
  }, [endTime]);

  // Check if a time range falls within any of the available slots
  const isWithinAvailableSlot = (startMins: number, endMins: number): { valid: boolean; slot?: TimeSlot } => {
    if (!availableSlots || availableSlots.length === 0) {
      return { valid: true }; // No slots defined, allow any time
    }

    for (const slot of availableSlots) {
      const slotStartMins = timeToMinutes(slot.startTime);
      const slotEndMins = timeToMinutes(slot.endTime);
      
      // Check if the selected range is completely within this slot
      if (startMins >= slotStartMins && endMins <= slotEndMins) {
        return { valid: true, slot };
      }
    }

    return { valid: false };
  };

  // Format available slots for display as array
  const getAvailableSlotsArray = (): string[] => {
    if (!availableSlots || availableSlots.length === 0) return [];
    return availableSlots.map(slot => 
      `${formatTimeDisplay(slot.startTime)} - ${formatTimeDisplay(slot.endTime)}`
    );
  };

  // Validate time range
  const validateTimeRange = (start: TimeValue, end: TimeValue): string | null => {
    const start24 = to24hFormat(start);
    const end24 = to24hFormat(end);
    const startMins = timeToMinutes(start24);
    const endMins = timeToMinutes(end24);

    // Check end time is after start time
    if (endMins <= startMins) {
      return 'End time must be after start time';
    }

    // If availableSlots is provided, validate against those
    if (availableSlots && availableSlots.length > 0) {
      const { valid } = isWithinAvailableSlot(startMins, endMins);
      if (!valid) {
        return 'SLOTS_ERROR'; // Special marker to show slots in list format
      }
      return null;
    }

    // Legacy: Check within allowed range (minTime/maxTime)
    if (minTime) {
      const minMins = timeToMinutes(minTime);
      if (startMins < minMins) {
        return `Start time cannot be before ${formatTimeDisplay(minTime)}`;
      }
    }

    if (maxTime) {
      const maxMins = timeToMinutes(maxTime);
      if (endMins > maxMins) {
        return `End time cannot be after ${formatTimeDisplay(maxTime)}`;
      }
    }

    return null;
  };

  const handleStartChange = (value: TimeValue) => {
    setStartValue(value);
    setError(null);
  };

  const handleEndChange = (value: TimeValue) => {
    setEndValue(value);
    setError(null);
  };

  const handleApply = () => {
    // Validate both times are selected
    if (!startValue.hour || !startValue.minute) {
      setError('Please select start time');
      return;
    }
    if (!endValue.hour || !endValue.minute) {
      setError('Please select end time');
      return;
    }

    const validationError = validateTimeRange(startValue, endValue);
    if (validationError) {
      setError(validationError);
      return;
    }

    onStartTimeChange(to24hFormat(startValue));
    onEndTimeChange(to24hFormat(endValue));
    setError(null);
    setOpen(false);
  };

  const handleReset = () => {
    setStartValue(getEmptyTimeValue());
    setEndValue(getEmptyTimeValue());
    onStartTimeChange('');
    onEndTimeChange('');
    setError(null);
  };

  const displayText = useMemo(() => {
    if (!startTime && !endTime) {
      return 'Select time range';
    }
    return `${formatTimeDisplay(startTime)} - ${formatTimeDisplay(endTime)}`;
  }, [startTime, endTime]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !startTime && !endTime && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>{displayText}</span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4 bg-background z-[100]" align="start">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Start Time */}
            <TimePicker
              value={startValue}
              onChange={handleStartChange}
              disabled={disabled}
              label="Start Time"
            />

            {/* End Time */}
            <TimePicker
              value={endValue}
              onChange={handleEndChange}
              disabled={disabled}
              label="End Time"
            />
          </div>

          {/* Error message */}
          {error && error !== 'SLOTS_ERROR' && (
            <p className="text-xs text-destructive">{error}</p>
          )}

          {/* Slots error with list format */}
          {error === 'SLOTS_ERROR' && (
            <div className="text-xs text-destructive space-y-1">
              <p>Selected time must be within available slots:</p>
              <ul className="list-disc list-inside ml-1 space-y-0.5">
                {getAvailableSlotsArray().map((slot, index) => (
                  <li key={index}>{slot}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Available slots hint */}
          {availableSlots && availableSlots.length > 0 && !error && (
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Available slots:</p>
              <ul className="list-disc list-inside ml-1 space-y-0.5">
                {getAvailableSlotsArray().map((slot, index) => (
                  <li key={index}>{slot}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Legacy: Available range hint */}
          {!availableSlots && (minTime || maxTime) && (
            <p className="text-xs text-muted-foreground">
              Available: {minTime ? formatTimeDisplay(minTime) : 'Any'} - {maxTime ? formatTimeDisplay(maxTime) : 'Any'}
            </p>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={disabled}
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              disabled={disabled}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TimeRangePicker;
